/**
 * Decodifica del payload JWT lato portale.
 *
 * IMPORTANTE — FIRMA NON VERIFICATA:
 * Questa funzione decodifica SOLO il payload base64url del JWT (seconda parte)
 * senza verificare la firma crittografica. Questo è intenzionale e corretto:
 * la verifica della firma è responsabilità esclusiva dei servizi Node backend
 * tramite @tenaciousc/auth (che usa jose per la verifica OIDC). Il portale usa
 * i claim decoded esclusivamente per decisioni UI (mostrare/nascondere sezioni).
 * Qualsiasi tentativo di manipolare il JWT sarà rilevato dal backend al momento
 * della chiamata API e risulterà in un 401/403.
 */

import type { CraRole, JwtClaims } from './types.js';

/**
 * Mapping esplicito gruppo IdP → ruolo CRA (TD-004 — login SSO).
 *
 * Duplica (di proposito, sola lettura lato client) `DEFAULT_GROUP_ROLE_MAP` da
 * `packages/auth/src/roles.ts`: il portale non importa pacchetti server-side.
 * Usato solo per il gating UI; l'autorizzazione effettiva resta server-side.
 */
const DEFAULT_GROUP_ROLE_MAP: Record<string, CraRole> = {
  'tenaciousc-analysts': 'security_analyst',
  'tenaciousc-developers': 'developer',
  'tenaciousc-analyst-supervisors': 'analyst_supervisor',
  'tenaciousc-admins': 'admin',
};

/**
 * Pattern per la convenzione `tenaciousc-<nome-ruolo>` (Task 1, specchio di
 * packages/auth/src/roles.ts). Permette ai ruoli custom di essere derivati
 * automaticamente dal gruppo IdP corrispondente.
 */
const TENACIOUSC_GROUP_RE = /^tenaciousc-([a-z][a-z0-9_]{2,63})$/;

/** Converte i gruppi IdP nei ruoli CRA corrispondenti (specchio di mapGroupsToRoles). */
function mapGroupsToRoles(groups: readonly string[]): CraRole[] {
  const roles = new Set<CraRole>();
  for (const g of groups) {
    // Prima prova il mapping esplicito.
    const explicit = DEFAULT_GROUP_ROLE_MAP[g];
    if (explicit) {
      roles.add(explicit);
      continue;
    }
    // Convenzione: tenaciousc-<nome-ruolo> → ruolo con quel nome.
    const match = TENACIOUSC_GROUP_RE.exec(g);
    if (match?.[1] && match[1] !== 'admin') roles.add(match[1]);
  }
  return [...roles];
}

/**
 * Decodifica il payload di un JWT (senza verifica firma).
 * Restituisce null se il token è malformato o il payload non è JSON valido.
 */
export function decodeJwtPayload(token: string): JwtClaims | null {
  try {
    // Un JWT è composto da tre parti separate da '.': header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payloadBase64 = parts[1];
    if (!payloadBase64) return null;

    // Converti da base64url a base64 standard
    const base64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
    // Aggiungi padding se necessario
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

    const decoded = atob(padded);
    const json = JSON.parse(decoded) as unknown;

    if (typeof json !== 'object' || json === null) return null;

    // Normalizza il campo `roles`: accetta array o stringa singola
    const raw = json as Record<string, unknown>;
    const rawRoles = raw['roles'];
    let roles: unknown[] = Array.isArray(rawRoles)
      ? rawRoles
      : typeof rawRoles === 'string'
        ? [rawRoles]
        : [];

    // id_token OIDC (TD-004): nessun claim `roles`, ma `groups` (assegnati dall'IdP).
    // Deriviamo i ruoli CRA dai gruppi con lo stesso mapping usato server-side.
    if (roles.length === 0) {
      const rawGroups = raw['groups'];
      const groups = Array.isArray(rawGroups)
        ? rawGroups.filter((g): g is string => typeof g === 'string')
        : typeof rawGroups === 'string'
          ? [rawGroups]
          : [];
      if (groups.length > 0) {
        roles = mapGroupsToRoles(groups);
      }
    }

    return {
      ...raw,
      sub: typeof raw['sub'] === 'string' ? raw['sub'] : '',
      roles: roles as JwtClaims['roles'],
    } as JwtClaims;
  } catch {
    return null;
  }
}

/** True se il token è scaduto (confronta `exp` con Date.now()). */
export function isTokenExpired(claims: JwtClaims): boolean {
  if (claims.exp === undefined) return false;
  return Date.now() / 1000 > claims.exp;
}
